import type { DishAllergen } from "api-types";

import { Stack, Typography, useTheme } from "@mui/material";

import { ErrorOutlineRounded } from "@mui/icons-material";

import {
  AllergenChip,
  AllergyIcon,
  ChoicesIcon,
  MayContainIcon,
} from "igfd-ui/src";

import {
  type AllergenBands,
  type CardChip,
  cardBands,
} from "../../menu/staff-allergen-bands";
import { staffMenuCopy } from "../../menu/staff-menu-copy";
import { BAND_ICON_SIZE, DrawerBand, DrawerSubBand } from "./drawer-band";

/**
 * The drawer's only allergen surface. An allergen in both the dish and an
 * option renders in both bands, deliberately.
 *
 * An empty band is dropped rather than answered with "None": three of those
 * stacked up read as a form nobody filled in. When every band is empty the card
 * says so once, because on a safety surface an unexplained absence reads as
 * nobody having looked.
 *
 * Every allergen is a checkbox, and ticking one marks each choice below as
 * suitable or as carrying it. That is the question a waiter actually has: not
 * what this dish contains, but whether the person at table six can have it and
 * what to change.
 */

export const AllergenBandsCard = ({
  bands,
  hasGroups,
  scope,
  selectedAllergenIds,
  onSelectAllergen,
}: {
  bands: AllergenBands;
  /** Whether there are options below for a tick to mark up. */
  hasGroups: boolean;
  /** Required: a default would let a header node render empty dish fields. */
  scope: "dish" | "group";
  selectedAllergenIds: string[];
  onSelectAllergen: (allergen: DishAllergen) => void;
}) => {
  const {
    palette: { oscar },
  } = useTheme();

  const isGroup = scope === "group";
  const {
    carried: carriedBand,
    mayContain: mayContainBand,
    options: optionBand,
    optionsMayContain: optionMayContainBand,
    reasons,
  } = cardBands(bands, scope);

  const carriedBandLabel = isGroup
    ? staffMenuCopy.bandInEveryOption
    : staffMenuCopy.bandContains;
  const optionBandLabel = isGroup
    ? staffMenuCopy.bandInSomeOptions
    : staffMenuCopy.bandInOptions;

  const hasChips =
    carriedBand.length > 0 ||
    mayContainBand.length > 0 ||
    optionBand.length > 0 ||
    optionMayContainBand.length > 0;
  // Only claim nothing is declared when the tree was fully readable. Saying it
  // above "1 choice can't be read" is two answers to one question.
  const nothingDeclared = !hasChips && bands.unreadable === 0;

  // `rowLabel` disambiguates for assistive tech: one allergen can render in
  // two bands, so the chips alone read as "Egg, Egg".
  const chips = (row: CardChip[], rowLabel: string) => (
    <Stack
      direction="row"
      gap={1}
      flexWrap="wrap"
      role="group"
      aria-label={rowLabel}
    >
      {row.map(({ allergen, removable, mayContain }) => (
        <AllergenChip
          key={allergen.igfdTaxAllergenId}
          allergenName={allergen.allergenName}
          action="select"
          hasAllergenIcon={false}
          hasActiveColor
          enabled
          isOptional={removable}
          mayContain={mayContain}
          selected={selectedAllergenIds.includes(allergen.igfdTaxAllergenId)}
          onClick={() => onSelectAllergen(allergen)}
          onDelete={() => {}}
        />
      ))}
    </Stack>
  );

  return (
    <Stack
      sx={{
        // The grey belongs to the section that groups this with the diet and
        // ingredient blocks. Painting its own made a card inside a card.
        // Shrinks in the drawer's scrolling column if left to. See dish-drawer.
        flexShrink: 0,
        gap: 2,
        width: "100%",
      }}
    >
      {/* Instructions for a control that has no chips to tick are noise. */}
      {hasChips ? (
        <Typography
          variant="labelS"
          sx={{ color: oscar.light.content.content2Text, fontStyle: "italic" }}
        >
          {hasGroups
            ? staffMenuCopy.bandsHint
            : staffMenuCopy.bandsHintNoGroups}
        </Typography>
      ) : null}

      {/* Nothing declared anywhere. Said once, because an allergen card that
          renders empty reads as nobody having checked. */}
      {nothingDeclared ? (
        <DrawerBand
          label={staffMenuCopy.bandNoAllergens}
          icon={
            <AllergyIcon
              height={BAND_ICON_SIZE}
              width={BAND_ICON_SIZE}
              // Not icon.mid, which the theme uses for a disabled glyph and is
              // too light to carry a statement on a safety surface.
              color={oscar.light.icon.dark}
            />
          }
        />
      ) : null}

      {carriedBand.length ? (
        <DrawerBand
          label={carriedBandLabel}
          icon={
            <AllergyIcon
              height={BAND_ICON_SIZE}
              width={BAND_ICON_SIZE}
              color={oscar.light.icon.danger}
            />
          }
        >
          {chips(carriedBand, carriedBandLabel)}
          {/* The fact the tree knows and the menu does not show. One reason per
              allergen, and only for allergens actually listed above: the walker
              propagates an option's own reasons up to its parent, so the raw
              list can explain something shown two bands lower, and can carry
              the same allergen twice at different depths. */}
          {reasons.map(({ allergenName, groupTitle }) => (
            <Typography
              key={allergenName}
              variant="labelS"
              color={oscar.light.content.content1}
            >
              {staffMenuCopy.unavoidableBecause(allergenName, groupTitle)}
            </Typography>
          ))}
        </DrawerBand>
      ) : null}

      {mayContainBand.length ? (
        <DrawerBand
          label={staffMenuCopy.bandMayContain}
          // The dashed ring is the house glyph for cross-contact: the same
          // circle as Contains, drawn as something less than certain.
          icon={
            <MayContainIcon height={BAND_ICON_SIZE} width={BAND_ICON_SIZE} />
          }
        >
          {chips(mayContainBand, staffMenuCopy.bandMayContain)}
        </DrawerBand>
      ) : null}

      {optionBand.length || optionMayContainBand.length ? (
        <DrawerBand
          label={optionBandLabel}
          icon={
            <ChoicesIcon
              height={BAND_ICON_SIZE}
              width={BAND_ICON_SIZE}
              color={oscar.light.icon.dark}
            />
          }
        >
          {/* A group's heading already states reach, so it needs no sub-rows.
              A dish splits carried from cross-contact, which flattened into
              one row read as all-carried. */}
          {isGroup ? (
            chips(optionBand, optionBandLabel)
          ) : (
            <Stack
              gap={1.5}
              sx={{
                pl: 2,
                ml: 1,
                borderLeft: `1px solid ${oscar.light.borders.gray}`,
              }}
            >
              {optionBand.length ? (
                <DrawerSubBand label={staffMenuCopy.bandContains}>
                  {chips(
                    optionBand,
                    `${optionBandLabel}: ${staffMenuCopy.bandContains}`
                  )}
                </DrawerSubBand>
              ) : null}
              {optionMayContainBand.length ? (
                <DrawerSubBand label={staffMenuCopy.bandMayContain}>
                  {chips(
                    optionMayContainBand,
                    `${optionBandLabel}: ${staffMenuCopy.bandMayContain}`
                  )}
                </DrawerSubBand>
              ) : null}
            </Stack>
          )}
        </DrawerBand>
      ) : null}

      {/* Once per card: marked chips can appear in two bands. */}
      {hasChips && bands.removable.size ? (
        <Typography variant="labelS" color={oscar.light.content.content1}>
          {staffMenuCopy.someRemovable}
        </Typography>
      ) : null}

      {/* Never dropped when non-zero: it exists to stop the bands above
          reading as a complete answer when they are not one. */}
      {bands.unreadable > 0 ? (
        <DrawerBand
          label={staffMenuCopy.bandUnreadable}
          // Unknown, not forbidden. The blocked glyph would overstate it.
          icon={
            <ErrorOutlineRounded
              sx={{
                color: oscar.light.content.danger,
                fontSize: BAND_ICON_SIZE,
              }}
            />
          }
        >
          <Typography
            variant="labelSMedium"
            color={oscar.light.content.dangerText}
          >
            {staffMenuCopy.bandUnreadableValue(bands.unreadable)}
          </Typography>
          <Typography variant="labelS" color={oscar.light.content.content1}>
            {staffMenuCopy.bandUnreadableNote}
          </Typography>
        </DrawerBand>
      ) : null}
    </Stack>
  );
};
